import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import ButtonComponent from "../generic/ButtonComponent";
import Modal from "../generic/Modal/Modal";
import { isKioskMode } from "../../helpers/posModeHelpers";
import { useFetchShopifyReauthUrl } from "../../hooks/shopifyReauthHooks";
import { API_V2_BASE_URL } from "../../constants/api";
import { useStoreDetails } from "../../hooks/storeHooks";

interface ShopifyTokenGuardProps {
  children: React.ReactNode;
}

function ShopifyTokenGuard({ children }: ShopifyTokenGuardProps) {
  const { customerId } = useStoreDetails();
  const queryClient = useQueryClient();
  const { data } = useFetchShopifyReauthUrl(!isKioskMode());
  const [reauthSuccess, setReauthSuccess] = useState(false);
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries(["shopifyReauthUrl"]);
      setReauthSuccess(true);
    };
    window.addEventListener("reauth-success", handler);
    return () => window.removeEventListener("reauth-success", handler);
  }, [queryClient]);

  const isOnAuthCallback = window.location.hash.startsWith("#/shopifyAppAuth");

  if (isKioskMode() || isOnAuthCallback) {
    return <>{children}</>;
  }

  if (reauthSuccess) {
    const dismiss = () => {
      setReauthSuccess(false);
      window.history.replaceState({}, document.title, window.location.hash);
    };
    return (
      <>
        <Modal onClose={dismiss}>
          <Modal.Header>Re-Authentication Successful</Modal.Header>
          <Modal.Content>
            <p>
              Your BinderPOS account has been successfully re-authenticated with
              Shopify.
            </p>
          </Modal.Content>
          <Modal.Actions>
            <ButtonComponent primary fullWidth onClick={dismiss}>
              Continue
            </ButtonComponent>
          </Modal.Actions>
        </Modal>
        {children}
      </>
    );
  }

  // Intentionally blocking: children are NOT rendered while reauth is required per spec.
  // (Contrast with the success branch above, where children render behind the modal.)
  if (data?.status === "reauth_required") {
    return (
      <Modal>
        <Modal.Header>Re-Authentication Required</Modal.Header>
        <Modal.Content>
          <p>
            Shopify has deprecated permanent API tokens and migration is now
            mandatory. Re-authenticate to maintain access to BinderPOS. Press
            the Re-Authenticate button to complete the process — it only takes a
            moment.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <ButtonComponent
            primary
            fullWidth
            onClick={() => {
              window.location.href = `${API_V2_BASE_URL}/shopifyApps/portal/reauth-redirect?customerId=${customerId}`;
            }}
          >
            Re-Authenticate
          </ButtonComponent>
        </Modal.Actions>
      </Modal>
    );
  }

  return <>{children}</>;
}

export default ShopifyTokenGuard;
